import axios from "axios";
import { AUTH_TOKEN, DOMAIN_ID, ORG_ID, REACT_APP_PAM_API } from "../constants";

// PAM-side calls the SSH proxy feature shares between the jump-server grid, the
// recordings list and the onboarding wizard.
//
// One footgun worth naming: the codebase exports REACT_APP_PAM_API and
// REACT_APP_API_PAM_URL, and TextRecording.js used to mix them on the same
// screen. Today both read the same env var (REACT_APP_PAM_API_URL), so a 404
// from one of these calls is a routing problem, not a base-URL one — check the
// path before blaming the constant. Everything here uses REACT_APP_PAM_API.
const pamHeaders = () => ({
  headers: { "X-Authorization": AUTH_TOKEN(), withCredentials: true },
});

const post = (path, payload) =>
  axios.post(`${REACT_APP_PAM_API}${path}`, payload, pamHeaders());

// { jumpServers: [...], total }
// Since SP-2 each row also carries the heartbeat-reported facts:
//   lastSeenAt, lastSeenIp, proxyVersion, proxyListen,
//   hostKeyFingerprint, backendPublicKey, caPublicKey
// All are null until the proxy reports them.
export function listJumpServer({ payload = {}, onSuccess, onFailure }) {
  return post("/jumpserver/listJumpServer", {
    pageId: 1,
    pageSize: 50,
    search: "",
    filter: { filterBy: "", value: "" },
    token: AUTH_TOKEN(),
    orgId: ORG_ID(),
    domainId: DOMAIN_ID(),
    ...payload,
  })
    .then((res) => onSuccess && onSuccess(res?.data))
    .catch((err) => onFailure && onFailure(err));
}

// Finds one jump-server row by id (or, failing that, by exact name).
// listJumpServer has no by-id lookup, so this pages through and matches
// client-side; the estate is small enough that one page is the normal case.
export function findJumpServer({ jumpServerId, serverName, pageSize = 200, onSuccess, onFailure }) {
  return listJumpServer({
    payload: { pageSize, ...(jumpServerId == null && serverName ? { search: serverName } : {}) },
    onSuccess: (data) => {
      const rows = data?.jumpServers || [];
      const hit =
        jumpServerId != null
          ? rows.find((r) => String(r.id ?? r.jumpServerId ?? r.Id) === String(jumpServerId))
          : serverName
            ? rows.find((r) => r.serverName === serverName)
            : null;
      onSuccess && onSuccess(hit || null, rows);
    },
    onFailure,
  });
}

// { sessionRecordings: [...], total }
// Since BE-1 each row may carry sessionId, jumpServerId, targetHost,
// targetAccount, identityMethod, presenceMethod and sizeBytes — all omitempty.
export function listSessionRecording({ payload = {}, onSuccess, onFailure }) {
  // Lowercase `s`. The capital-S spelling 404s; this one reaches the handler
  // (401 without a token), which is how the difference was found.
  return post("/sessionRecording/listSessionRecording", {
    orgId: ORG_ID(),
    domainId: DOMAIN_ID(),
    pageId: 1,
    pageSize: 10,
    search: "",
    filter: { filterBy: "", value: "" },
    ...payload,
  })
    .then((res) => onSuccess && onSuccess(res?.data))
    .catch((err) => onFailure && onFailure(err));
}

// { orgId, id } → { code, status, id, sessionId, recordingMimeType, sizeBytes,
//                   content (base64), integrity, hmac }
//
// The bytes come back through the API rather than a presigned URL: SSH proxy
// rows leave recordingUrl empty. `content` is base64 because a terminal
// recording carries control bytes and partial UTF-8 that do not survive a JSON
// string.
export function getRecordingContent({ id, onSuccess, onFailure }) {
  return post("/sessionRecording/getRecordingContent", { orgId: ORG_ID(), id })
    .then((res) => onSuccess && onSuccess(res?.data))
    .catch((err) => onFailure && onFailure(err));
}

// Assign (or clear) the gateway an endpoint is reachable through.
// instanceId is the endpoint's machine_id. PUT, not POST.
export function updateEndpointGateway({ instanceId, jumpServerId, onSuccess, onFailure }) {
  return axios
    .put(
      `${REACT_APP_PAM_API}/instances/updateGateway`,
      { instanceId: Number(instanceId), jumpServerId: Number(jumpServerId) || 0 },
      pamHeaders(),
    )
    .then((res) => onSuccess && onSuccess(res?.data))
    .catch((err) => onFailure && onFailure(err));
}

// Registers an agentless endpoint. osId 2 is Linux — the SSH proxy only reaches
// machines running sshd. addInstance marks it Active and rejects a gateway id
// that does not exist. Resolves with the new endpoint's id as a number; the API
// returns it as a string under "InstanceId".
export function addInstance({ instanceName, privateIp, publicIp = "", jumpServerId, onSuccess, onFailure }) {
  return post("/instances/addInstance", {
    instanceName,
    osId: 2,
    privateIp,
    publicIp: publicIp || "",
    jumpServerId: Number(jumpServerId) || 0,
    domainId: DOMAIN_ID(),
    orgId: ORG_ID(),
  })
    .then((res) => onSuccess && onSuccess(Number(res?.data?.InstanceId ?? res?.data?.instanceId) || null, res?.data))
    .catch((err) => onFailure && onFailure(err));
}

// An endpoint's local accounts: the ones found by the account scan (source
// "gateway"; re-scanned daily) plus any recorded by hand ("manual"). Older
// agent-onboarded machines may report other sources.

// Sets the endpoint's MANUAL accounts to exactly `accounts` (empty clears them).
// Agent-reported accounts are never changed. Administrator only.
// Resolves with { instanceId, accounts: [{ name, source }] }.
export function setEndpointAccounts({ instanceId, accounts, onSuccess, onFailure }) {
  return post("/instances/setAccounts", { instanceId: Number(instanceId), accounts: accounts || [] })
    .then((res) => onSuccess && onSuccess(res?.data || {}))
    .catch((err) => onFailure && onFailure(err));
}

// Accounts for several endpoints at once, one entry per id in request order.
// Resolves with [{ instanceId, accounts: [{ name, source }] }].
export function listEndpointAccounts({ instanceIds, onSuccess, onFailure }) {
  const ids = (instanceIds || []).map(Number).filter((n) => n > 0);
  if (!ids.length) {
    onSuccess && onSuccess([]);
    return Promise.resolve();
  }
  return post("/instances/listAccounts", { instanceIds: ids })
    .then((res) => onSuccess && onSuccess(res?.data?.endpoints || []))
    .catch((err) => onFailure && onFailure(err));
}

// Same rule the backend enforces: the portable POSIX username set sshd accepts
// as a principal, no leading '-', at most 32 characters, and never '@' — ssh
// reads it as the host separator in `ssh account@host@gateway`. Returns an
// error message, or null when the name is fine.
export const MAX_ENDPOINT_ACCOUNTS = 100;
export function accountNameError(name) {
  const n = String(name || "").trim();
  if (!n) return null;
  if (n.includes("@")) return `"${n}" contains '@', which ssh reads as the host separator`;
  if (n.length > 32) return `"${n}" is longer than 32 characters`;
  if (n.startsWith("-")) return `"${n}" must not start with '-'`;
  if (!/^[A-Za-z0-9._-]+$/.test(n)) return `"${n}" — use letters, digits, '.', '_' or '-'`;
  return null;
}

// SSH grants: the key a policy holds on one account of one endpoint. Created
// and ended as policies are saved, suspended and deleted.
// Each grant: { id, policyId, endpointId, account, jumpServerId,
//   status: "pending" | "active" | "failed" | "disabled",
//   keyFingerprint (once active), disabledReason, createdAt, activatedAt, disabledAt }
// Filter by policyId, instanceId, or both.
export function listGrants({ policyId, instanceId, onSuccess, onFailure }) {
  return post("/instances/listGrants", {
    ...(policyId ? { policyId: String(policyId) } : {}),
    ...(instanceId ? { instanceId: Number(instanceId) } : {}),
  })
    .then((res) => onSuccess && onSuccess(res?.data?.grants || []))
    .catch((err) => onFailure && onFailure(err));
}

// Re-runs one policy's grant sync (administrator only): the retry when keys
// failed at save time, or once a named host has been given a gateway.
// → { created, kept, disabled, skipped: ["host x: unknown", …] }
export function syncGrants({ policyId, onSuccess, onFailure }) {
  return post("/instances/syncGrants", { policyId: String(policyId) })
    .then((res) => onSuccess && onSuccess(res?.data || {}))
    .catch((err) => onFailure && onFailure(err));
}

// Accounts that never get a key; the backend skips them. System accounts
// (uid < 1000) are skipped too, but listAccounts doesn't report uids.
export const NO_KEY_ACCOUNTS = ["root", "authnull-svc", "nobody"];

// ─── Endpoint setup and account scans ────────────────────────────────────────
// Setup: issues the key Authnull's authnull-svc account uses on the machine and
// returns the one-off script to run there as root. Administrator only. Queues
// the first account scan when the endpoint has a gateway (scanJobId).
// → { instanceId, account, keyFingerprint, publicKey, script, scanJobId? }
// Re-running issues a NEW key — it is for recovering a machine.
export function reconcileSetup({ instanceId, onSuccess, onFailure }) {
  return post("/instances/reconcileSetup", { instanceId: Number(instanceId) })
    .then((res) => onSuccess && onSuccess(res?.data || {}))
    .catch((err) => onFailure && onFailure(err));
}

// Queues an account scan now (it also runs daily). → { jobId, gatewayId }.
// 409: the endpoint has no gateway assigned.
export function scanAccounts({ instanceId, onSuccess, onFailure }) {
  return post("/instances/scanAccounts", { instanceId: Number(instanceId) })
    .then((res) => onSuccess && onSuccess(res?.data || {}))
    .catch((err) => onFailure && onFailure(err));
}

// The latest scan of an endpoint:
// { jobId, status: "queued"|"claimed"|"done"|"failed"|"expired", attempts,
//   queuedAt, completedAt?, error? } — or { status: "never" } before any scan.
export function scanStatus({ instanceId, onSuccess, onFailure }) {
  return post("/instances/scanStatus", { instanceId: Number(instanceId) })
    .then((res) => onSuccess && onSuccess(res?.data || {}))
    .catch((err) => onFailure && onFailure(err));
}

// The latest gateway job of one kind on an endpoint (scan_accounts,
// decommission, rotate_credential, revoke_credential).
// → { status: "never" } | { jobId, status: "queued"|"claimed"|"done"|"failed"|"expired",
//     attempts, queuedAt, completedAt?, error? }
export function jobStatus({ instanceId, kind, onSuccess, onFailure }) {
  return post("/instances/jobStatus", { instanceId: Number(instanceId), kind })
    .then((res) => onSuccess && onSuccess(res?.data || {}))
    .catch((err) => onFailure && onFailure(err));
}

// ─── Decommission and recommission ───────────────────────────────────────────
// Decommission (administrator only) ends every grant on the endpoint and stops
// it being listed to its gateway at once, then queues a last gateway job that
// removes Authnull's setup from the machine.
// → { status: "decommissioning", jobId, grantsEnded } — follow it with
//   jobStatus(kind "decommission"); the endpoint is Decommissioned when it's done.
// → { status: "decommissioned", grantsEnded, machineCleaned: false, message, cleanupFiles }
//   with force, and also WITHOUT force when the endpoint has no gateway or was
//   never set up: finished in Authnull only, `cleanupFiles` is what to remove by hand.
// Both answers carry passwordsKept: [account] — rotated passwords left in the vault.
// → { status: "decommissioned", message: "already decommissioned" }
// 403 not an administrator; 404 no such endpoint.
export function decommissionEndpoint({ instanceId, force = false, onSuccess, onFailure }) {
  return post("/instances/decommission", { instanceId: Number(instanceId), force: !!force })
    .then((res) => onSuccess && onSuccess(res?.data || {}))
    .catch((err) => onFailure && onFailure(err));
}

// Brings a Decommissioned endpoint back (administrator only): Active again, a
// new setup key and script (decommissioning removed the old setup), the first
// scan queued.
// → { status: "Active", instanceId, account, publicKey, keyFingerprint, script, scanJobId? }
//   — the same shape as reconcileSetup.
// 409: still decommissioning, not decommissioned, or no gateway assigned.
// 503: no vault. 500 "active again, but its setup could not be issued": the
// endpoint IS Active then; Re-run setup issues the script.
export function recommissionEndpoint({ instanceId, onSuccess, onFailure }) {
  return post("/instances/recommission", { instanceId: Number(instanceId) })
    .then((res) => onSuccess && onSuccess(res?.data || {}))
    .catch((err) => onFailure && onFailure(err));
}

// An endpoint's lifecycle from its status: "decommissioning" while the gateway
// cleans the machine up, "decommissioned" once it has (or was forced), else null.
export const endpointLifecycle = (status) => {
  const s = String(status || "").trim().toLowerCase();
  return s === "decommissioning" || s === "decommissioned" ? s : null;
};

// A queued scan whose error is only "the setup script hasn't been run yet":
// the gateway can't log in until it has. Normal while waiting, not a failure.
export const scanWaitingForSetup = (st) =>
  !!st &&
  (st.status === "queued" || st.status === "claimed") &&
  Number(st.attempts) > 0 &&
  /handshake failed|unable to authenticate/i.test(String(st.error || ""));

// ─── Enrolment tokens (fleets) ───────────────────────────────────────────────
// One token, bound to a gateway, lets many machines onboard themselves: each
// runs the same command and appears under Endpoints. Administrator only.
export const ENROL_MAX_TTL_HOURS = 168;
export const ENROL_MAX_USES = 1000;

// → { token, tokenInfo, command? | commandUnavailable? }. The token and command
// are returned only now. commandUnavailable: the gateway hasn't reported its
// host key yet (or has no address on record).
export function createEnrolToken({ jumpServerId, name, ttlHours, maxUses, onSuccess, onFailure }) {
  return post("/instances/enrolTokens/create", {
    jumpServerId: Number(jumpServerId),
    name: String(name || "").trim(),
    ttlHours: Number(ttlHours) || 0,
    maxUses: Number(maxUses) || 0,
  })
    .then((res) => onSuccess && onSuccess(res?.data || {}))
    .catch((err) => onFailure && onFailure(err));
}

// → [{ id, jumpServerId, name?, expiresAt, maxUses, uses, createdAt, revokedAt? }]
export function listEnrolTokens({ jumpServerId, onSuccess, onFailure }) {
  return post("/instances/enrolTokens/list", jumpServerId ? { jumpServerId: Number(jumpServerId) } : {})
    .then((res) => onSuccess && onSuccess(res?.data?.tokens || []))
    .catch((err) => onFailure && onFailure(err));
}

// 404: no live token with that id (already revoked or expired).
export function revokeEnrolToken({ id, onSuccess, onFailure }) {
  return post("/instances/enrolTokens/revoke", { id: String(id) })
    .then((res) => onSuccess && onSuccess(res?.data || {}))
    .catch((err) => onFailure && onFailure(err));
}

// ─── Gateways ────────────────────────────────────────────────────────────────
// Status is automatic (IN PROGRESS until the gateway checks in, then ACTIVE);
// admins can set DISABLED, which cuts the gateway off at once, or ACTIVE to
// re-enable it. Non-admins get 403.
export function updateGatewayStatus({ id, status, onSuccess, onFailure }) {
  return axios
    .put(`${REACT_APP_PAM_API}/jumpserver/updateStatus`, { id: Number(id), status }, pamHeaders())
    .then((res) => onSuccess && onSuccess(res?.data))
    .catch((err) => onFailure && onFailure(err));
}

// What the signed-in operator can reach: one row per account on a machine,
// each with the exact connection command.
// → { principal, targets: [{ account, host, hostname, endpointId, gatewayId,
//      gateway, gatewayAddress, port, policy, policyId, approval, command }] }
export function myTargets({ onSuccess, onFailure }) {
  return post("/ssh/myTargets", {})
    .then((res) => onSuccess && onSuccess({ principal: res?.data?.principal || "", targets: res?.data?.targets || [] }))
    .catch((err) => onFailure && onFailure(err));
}

// The SSH access log: one row per access attempt, newest first. Administrator
// only (403 otherwise). All fields optional: { from, to (RFC 3339; default the
// last 24 h), operator, target, gatewayId, outcome (approved | denied | refused
// | no_answer | failed | live), limit (≤ 200, default 50), cursor, bucketMs }.
// → { rows, counts, timeline, bucketMs, nextCursor } — counts and timeline
// cover the whole window under the same filters (counts ignore outcome).
export function sshAccessLog({ payload = {}, onSuccess, onFailure }) {
  return post("/ssh/accessLog", payload)
    .then((res) => onSuccess && onSuccess(res?.data || {}))
    .catch((err) => onFailure && onFailure(err));
}

// ─── Password rotation ───────────────────────────────────────────────────────
// Local account passwords rotated by the gateway and kept in the vault.
// Administrator only (403), except passwordSsh. Errors are {error}, readable.
const call = (path) => ({ onSuccess, onFailure, ...body } = {}) =>
  post(path, body)
    .then((res) => onSuccess && onSuccess(res?.data || {}))
    .catch((err) => onFailure && onFailure(err));

// { instanceId: 0 = all, policyId: "" } → { accounts: [Account] } (never passwords)
export const listPasswordAccounts = call("/passwords/listAccounts");
// { endpointId, account } → { jobId }
export const rotatePasswordNow = call("/passwords/rotateNow");
// { endpointId, account } → { revealId, waitSeconds }; 502 when the push can't be sent
export const revealPasswordStart = call("/passwords/revealStart");
// { revealId } → { status: pending | denied | expired | shown | approved, password?, unconfirmed?, showSeconds?, rerotateAt? }
export const revealPasswordResult = call("/passwords/revealResult");
// { endpointId, account, credential: "key" | "password" | "" } → { inUse }
export const setAccountCredential = call("/passwords/setCredential");
// { instanceId } → { state, allow: [string], needsSetup }
export const getPasswordSsh = call("/instances/passwordSsh");
// { instanceId } → { status, hostname, accessMode: "svc" | "admin", adminAccount?, activeGrants,
//   selfCleanup, cleanupFiles: [string], keptPasswords: [KeptPassword] } — for the decommission dialog.
export const decommissionPreview = call("/instances/decommissionPreview");
// { instanceId } → { status, passwords: [KeptPassword] }, in any state of the endpoint.
// KeptPassword: { account, rotatedAt?, unconfirmed } — unconfirmed: a rotation was under way.
export const keptPasswords = call("/instances/keptPasswords");
// { instanceId, allow: [string] (≤ 8) } → { allow, scanJobId }
export const setPasswordSsh = call("/instances/setPasswordSsh");

// Endpoints onboarded with an existing admin (sudo) account. Send exactly one
// of password or privateKey; never keep either in state or storage after.
// { instanceId, username, password | privateKey } → { instanceId, username, method, jobId }
export const setAdminCredential = call("/instances/adminCredential");
// { instanceId } → { mode: "admin" | "svc", username?, credentialUpdatedAt?,
//   bootstrap?: { status: queued | claimed | done | failed | expired, error?, createdAt, completedAt? } }
export const getAdminCredentialStatus = call("/instances/adminCredentialStatus");
// { instanceId } → { instanceId, jobId }; 409 unless onboarded with an admin account
export const rerunAdminBootstrap = call("/instances/adminBootstrap");
// { instanceId } → { users: [LocalUser], helperVersion, canAddPermanent, defaultIntervalDays, minHours, maxDays }
export const listLocalUsers = call("/instances/localUsers");
// { instanceId, account, enabled, intervalDays } → { rotation: { endpointId, account, enabled, intervalDays } }
export const setAccountRotation = call("/passwords/setAccountRotation");
// Connect → Open terminal. { instanceId, account } → { ticket (single use), wsPath, expiresIn, account, host, hostname };
// 403 {error} when the person may not open it. The socket is REACT_APP_API (ws) + wsPath + "?ticket=…".
export const terminalStart = call("/instances/terminalStart");

// One readable message for any of the calls above.
export const passwordErrorText = (err, fallback = "Something went wrong.") =>
  err?.response?.status === 403 ? "Administrator role required." : err?.response?.data?.error || fallback;

// The reserved account an Endpoint Policy names to grant temporary users.
export const TEMP_ACCOUNT = "temp";

// Added users (managed accounts): a local account Authnull creates, logging in
// with a key or a vaulted, rotated password, removed at its expiry if it has one.
// Rows go pending → active → removing → removed, or pending → failed.
// Administrator only. Errors are {error}, readable.
// { instanceId (0 = all) } → { managedAccounts: [ManagedAccount], helperVersion, minHours, maxDays }
export function listManagedAccounts({ instanceId = 0, onSuccess, onFailure }) {
  return post("/managedAccounts/list", { instanceId: Number(instanceId) || 0 })
    .then((res) => onSuccess && onSuccess(res?.data || {}))
    .catch((err) => onFailure && onFailure(err));
}

// Add user: { instanceId, account, credential: "key" | "password", intervalDays (password only),
// expiresAt: "" (never) or RFC 3339, note? } → { managedAccount }. Pending, then active in about a minute.
// 400 bad name / expiry / interval, or no expiry before setup is re-run; 409 the endpoint already has that account.
export function createManagedAccount({ instanceId, account, credential = "key", intervalDays, expiresAt = "", note, onSuccess, onFailure }) {
  return post("/managedAccounts/create", {
    instanceId: Number(instanceId),
    account: String(account || "").trim(),
    credential,
    intervalDays: credential === "password" ? Number(intervalDays) || 30 : 0,
    expiresAt: expiresAt || "",
    note: String(note || "").trim(),
  })
    .then((res) => onSuccess && onSuccess(res?.data || {}))
    .catch((err) => onFailure && onFailure(err));
}

// { id } → { status: "removing" }; 409 when it's already being removed.
export function removeManagedAccount({ id, onSuccess, onFailure }) {
  return post("/managedAccounts/remove", { id })
    .then((res) => onSuccess && onSuccess(res?.data || {}))
    .catch((err) => onFailure && onFailure(err));
}

// Every endpoint, unfiltered. Callers narrow by jumpServerId.
export function listInstances({ onSuccess, onFailure }) {
  return post("/instances/list", {
    domainId: DOMAIN_ID(),
    pageId: 1,
    pageSize: 500,
    search: "",
    filter: { filterBy: "", value: "" },
    token: AUTH_TOKEN(),
    orgId: ORG_ID(),
    MachineType: "endpoint",
  })
    .then((res) => onSuccess && onSuccess(res?.data?.instances || []))
    .catch((err) => onFailure && onFailure(err));
}

// One endpoint, re-read from the list. CaTrustPanel keeps its row in
// localStorage, which goes stale the moment the gateway is reassigned.
export function findInstance({ instanceId, onSuccess, onFailure }) {
  return post("/instances/list", {
    domainId: DOMAIN_ID(),
    pageId: 1,
    pageSize: 500,
    search: "",
    filter: { filterBy: "", value: "" },
    token: AUTH_TOKEN(),
    orgId: ORG_ID(),
    MachineType: "endpoint",
  })
    .then((res) => {
      const rows = res?.data?.instances || [];
      onSuccess && onSuccess(rows.find((r) => String(r.instanceId) === String(instanceId)) || null);
    })
    .catch((err) => onFailure && onFailure(err));
}

// What a user may connect to, and the exact command for each target:
// { connections: [{ host, account, jumpServer, jumpServerId, command, live,
// policyId, policyName }], unscoped?, message? }. Administrators may ask about
// any userId. Render `command` as returned — ssh splits on the last @, so a
// client-built command is easy to get subtly wrong.
export function listSSHConnections({ userId, onSuccess, onFailure }) {
  return post("/ssh/listConnections", { userId: Number(userId) || 0 })
    .then((res) => onSuccess && onSuccess(res?.data || {}))
    .catch((err) => onFailure && onFailure(err));
}

export function downloadGatewayConfig({ jumpServerId, onSuccess, onFailure }) {
  return post("/jumpserver/downloadConfig", { jumpServerId: Number(jumpServerId) })
    .then((res) => onSuccess && onSuccess(res?.data))
    .catch((err) => onFailure && onFailure(err));
}

// base64 → string, preserving the bytes. atob gives one char per byte; decoding
// those bytes as UTF-8 is what keeps box-drawing characters and accented output
// intact instead of turning them into mojibake.
export function decodeBase64Content(b64) {
  if (!b64) return "";
  const binary = atob(String(b64).replace(/\s+/g, ""));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  try {
    return new TextDecoder("utf-8").decode(bytes);
  } catch {
    return binary;
  }
}

// PAM dashboard counts (admins only; 403 otherwise). → { gateways, endpoints,
// policies, live, last24h: { …, activity: 24 hourly buckets, oldest first },
// generatedAt }.
export function dashboardSummary({ onSuccess, onFailure } = {}) {
  return post("/dashboard/summary", {})
    .then((res) => onSuccess && onSuccess(res?.data || null))
    .catch((err) => onFailure && onFailure(err));
}
